import { useState, type CSSProperties } from "react";

import { cn } from "@/lib/cn";
import type { StaticEntityType } from "@config";
import { useIsPdf } from "@library/components";
import { SandboxedHtml } from "../../embedded/components/SandboxedHtml";
import { REGEX } from "../constants";
import { CalendarStyled } from "../styles/Calendar.styled";
import type {
  CalendarProps,
  EditorDataProps,
  EditorSlotsProps,
} from "../types";
import { Preview } from "./Preview";

export const CalendarLibrary = ({
  entity,
  editorSlots,
  editorData,
}: {
  entity: StaticEntityType;
  editorSlots?: EditorSlotsProps;
  editorData?: EditorDataProps;
}) => {
  const { settings } = entity as StaticEntityType<CalendarProps>;
  const width = editorData?.width || settings.width;
  const height = editorData?.height || settings.height;

  const [active, setActive] = useState<string>(
    settings.code.startsWith("http") ? "link" : "code",
  );
  const isPdf = useIsPdf();

  const [prevCode, setPrevCode] = useState(settings.code);
  if (settings.code !== prevCode) {
    setPrevCode(settings.code);
    setActive(settings.code.startsWith("http") ? "link" : "code");
  }

  return (
    <CalendarStyled
      $horizontalAlignment={settings.horizontalAlignment}
      $round={settings.round}
      style={
        {
          "--entity-size": `${Number(width)}%`,
          "--entity-height": `${settings.view === "preview" || isPdf ? "auto" : height + "px"}`,
        } as CSSProperties
      }
      ref={editorData?.containerRef}
      className="tw-relative tw-flex"
    >
      {editorSlots?.resizeSlot}
      <div
        className={cn(
          "iframe__wrapper tw-overflow-hidden max-md:tw-w-full",
          !!editorData && "tw-pointer-events-none",
          settings.isShadow && "tw-shadow-[rgba(0,_0,_0,_0.15)_0px_4px_8px]",
        )}
        {...EDITOR_ATTRIBUTES({ isEditor: !!editorData })}
      >
        {(settings.view === "preview" && settings.type !== "custom") ||
        isPdf ? (
          <Preview entity={entity} />
        ) : (
          <>
            {active === "link" && REGEX.test(settings.code) && (
              <iframe
                src={settings.code}
                title="URL link"
                className="tw-h-full tw-w-full tw-border-none"
              ></iframe>
            )}
            {active === "code" && settings.code && (
              <SandboxedHtml
                code={settings.code}
                title="Embedded calendar"
                className="code tw-h-full tw-w-full tw-border-none"
              />
            )}
            {!settings.code && editorSlots?.emptySlot}
          </>
        )}
      </div>
    </CalendarStyled>
  );
};

const EDITOR_ATTRIBUTES = ({ isEditor }: { isEditor: boolean }) => {
  const payload: Record<string, string> = {
    "data-dnd-trigger": "true",
  };
  return isEditor ? payload : {};
};
